import React, { useState, useMemo } from 'react';
import { Search, Filter, Star, ShoppingCart, MapPin, Truck } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import usePageTitle from '../hooks/usePageTitle';

const ProductCard = ({ name, shop, price, originalPrice, rating, reviews, image, category, description, t }) => (
  <div className="glow-card bg-dark-card border border-dark-border rounded-xl p-4 hover:border-dark-border-light transition-all duration-400 cursor-pointer group">
    <div className="relative mb-3 bg-dark-surface rounded-lg overflow-hidden aspect-square flex items-center justify-center border border-dark-border">
      <div className="text-4xl opacity-80">🛍️</div>
      <div className="absolute top-2 right-2 bg-dark-elevated text-accent text-[10px] font-bold px-2 py-1 rounded border border-dark-border">
        {category}
      </div>
    </div>
    <div className="mb-2">
      <h3 className="text-sm font-medium text-white line-clamp-2 mb-1 group-hover:text-accent transition-colors">
        {name}
      </h3>
      <p className="text-xs text-gray-500 line-clamp-1">{description}</p>
      <div className="flex items-center mt-1">
        <span className="text-xs text-gray-400 flex items-center">
          <MapPin className="h-3 w-3 mr-1" />
          {shop}
        </span>
      </div>
    </div>
    <div className="flex items-center mb-2">
      <div className="flex items-center">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`h-3 w-3 ${i < Math.floor(rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-600'}`}
          />
        ))}
      </div>
      <span className="text-xs text-gray-500 ml-1">({reviews})</span>
    </div>
    <div className="flex items-baseline justify-between mb-3">
      <div>
        <span className="text-lg font-bold text-white">${price}</span>
        {originalPrice && (
          <span className="text-sm text-gray-500 line-through ml-2">${originalPrice}</span>
        )}
      </div>
      <div className="flex items-center text-xs text-emerald-400">
        <Truck className="h-3 w-3 mr-1" />
        {t('shopping.delivery')}
      </div>
    </div>
    <button className="w-full bg-dark-surface border border-dark-border text-gray-300 py-2 rounded-lg text-sm font-medium hover:bg-dark-elevated hover:text-white transition-all duration-300 flex items-center justify-center">
      <ShoppingCart className="h-4 w-4 mr-1" />
      {t('shopping.addToCart')}
    </button>
  </div>
);

const Shopping = () => {
  const { t, language } = useLanguage();
  usePageTitle('Shopping');
  const categories = useMemo(() => {
    const categoriesValue = t('shopping.categories');
    return Array.isArray(categoriesValue) ? categoriesValue : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);
  const [selectedCategory, setSelectedCategory] = useState(categories[0] || '全部');
  
  const categoryMap = useMemo(() => {
    const chineseCategories = ['美食餐饮', '日用百货', '生鲜蔬果', '服装鞋帽', '电子产品', '家居用品', '美妆护理'];
    const map = {};
    chineseCategories.forEach((chineseCat, index) => {
      if (categories[index + 1]) {
        map[chineseCat] = categories[index + 1];
      }
    });
    return map;
  }, [categories]);
  
  React.useEffect(() => {
    setSelectedCategory(categories[0] || '全部');
  }, [language, categories]);

  const products = [
    {
      name: '四川麻辣火锅底料 500g',
      shop: '法拉盛川味坊',
      price: '12.99',
      originalPrice: '15.99',
      rating: 4.5,
      reviews: '234',
      category: '美食餐饮',
      description: '正宗四川口味，麻辣鲜香'
    },
    {
      name: '东北大米 10kg',
      shop: '唐人街粮油店',
      price: '18.99',
      rating: 4.8,
      reviews: '456',
      category: '日用百货',
      description: '优质东北大米，颗粒饱满'
    },
    {
      name: '新鲜大白菜',
      shop: 'Flushing菜市场',
      price: '2.99',
      rating: 4.6,
      reviews: '128',
      category: '生鲜蔬果',
      description: '当天新鲜采摘，有机种植'
    },
    {
      name: '中式传统旗袍',
      shop: '旗袍定制店',
      price: '89.99',
      originalPrice: '120.00',
      rating: 4.7,
      reviews: '89',
      category: '服装鞋帽',
      description: '传统工艺，多种尺寸可选'
    },
    {
      name: '中文儿童图书 10本套装',
      shop: '华人书店',
      price: '24.99',
      rating: 4.9,
      reviews: '312',
      category: '日用百货',
      description: '适合3-8岁儿童阅读'
    },
    {
      name: '手工饺子 30个',
      shop: '手工饺子坊',
      price: '15.99',
      rating: 4.8,
      reviews: '567',
      category: '美食餐饮',
      description: '韭菜猪肉馅，纯手工制作'
    },
    {
      name: '茶叶礼盒装',
      shop: '茶叶专营店',
      price: '35.99',
      originalPrice: '45.99',
      rating: 4.6,
      reviews: '145',
      category: '美食餐饮',
      description: '精选茶叶，送礼佳品'
    },
    {
      name: '中式炒锅 32cm',
      shop: '厨具专卖店',
      price: '39.99',
      rating: 4.7,
      reviews: '278',
      category: '家居用品',
      description: '不粘涂层，适用电磁炉'
    },
    {
      name: '珍珠奶茶材料包',
      shop: '奶茶原料店',
      price: '8.99',
      rating: 4.5,
      reviews: '198',
      category: '美食餐饮',
      description: '自制珍珠奶茶，简单易做'
    },
    {
      name: '中式红木筷子 10双装',
      shop: '餐具专营店',
      price: '12.99',
      rating: 4.4,
      reviews: '167',
      category: '家居用品',
      description: '优质红木，精美雕刻'
    },
    {
      name: '新鲜豆腐 4块装',
      shop: '豆腐坊',
      price: '3.99',
      rating: 4.8,
      reviews: '234',
      category: '生鲜蔬果',
      description: '当天制作，新鲜嫩滑'
    },
    {
      name: '中文书法用品套装',
      shop: '文房四宝店',
      price: '29.99',
      rating: 4.6,
      reviews: '92',
      category: '日用百货',
      description: '笔墨纸砚，初学者适用'
    },
  ];

  const filteredProducts = selectedCategory === categories[0]
    ? products 
    : products.filter(p => {
        const translatedCategory = categoryMap[p.category] || p.category;
        return translatedCategory === selectedCategory;
      });

  return (
    <div className="-mt-16">
      <section className="relative pt-36 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-mesh opacity-50" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-gradient-to-br from-amber-600/12 via-orange-600/8 to-transparent rounded-full blur-[100px]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
            <h1 className="text-3xl md:text-4xl font-bold text-white">{t('shopping.title')}</h1>
            <span className="inline-flex items-center px-3.5 py-1.5 text-[11px] font-semibold uppercase text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-full">
              {t('shopping.inDevelopment')}
            </span>
            <p className="text-gray-400 hidden md:block md:ml-auto">{t('home.features.shopping.description')}</p>
          </div>
          <p className="text-gray-400 mb-6 md:hidden">{t('home.features.shopping.description')}</p>
          <div className="relative max-w-2xl group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"><Search className="h-5 w-5 text-gray-500 group-focus-within:text-accent transition-colors" /></div>
            <input className="block w-full pl-12 pr-4 py-3 bg-dark-card border border-dark-border text-white placeholder-gray-500 rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-accent/50 outline-none text-base transition-all" placeholder={t('shopping.searchPlaceholder')} type="search" />
          </div>
        </div>
      </section>

      <section className="relative py-8 md:py-12 min-h-[60vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="lg:w-64 flex-shrink-0">
              <div className="bg-dark-card rounded-2xl border border-dark-border p-4 sticky top-24 shadow-dark-card">
                <div className="flex items-center mb-4">
                  <Filter className="h-5 w-5 text-accent mr-2" />
                  <h2 className="font-semibold text-white">{t('shopping.filter')}</h2>
                </div>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-300 mb-3">{t('shopping.categoryTitle')}</h3>
                    <div className="space-y-2">
                      {categories.map((cat) => (
                        <button key={cat} onClick={() => setSelectedCategory(cat)} className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-300 ${selectedCategory === cat ? 'bg-accent/15 text-accent border border-accent/25' : 'bg-dark-surface text-gray-400 border border-dark-border hover:text-white hover:border-dark-border-light'}`}>
                          {cat}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex-grow">
              <div className="mb-4 flex items-center justify-between">
                <div className="text-sm text-gray-400">{t('shopping.foundProducts', { count: filteredProducts.length })}</div>
                <select className="border border-dark-border rounded-xl px-3 py-1.5 text-sm focus:ring-2 focus:ring-accent/30 focus:border-accent/50 outline-none bg-dark-card text-white">
                  <option>{t('shopping.sort.newest')}</option>
                  <option>{t('shopping.sort.priceLow')}</option>
                  <option>{t('shopping.sort.priceHigh')}</option>
                  <option>{t('shopping.sort.rating')}</option>
                  <option>{t('shopping.sort.sales')}</option>
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredProducts.map((product, index) => (
                  <ProductCard key={index} {...product} category={categoryMap[product.category] || product.category} t={t} />
                ))}
              </div>
              <div className="mt-8 flex justify-center items-center space-x-2">
                <button className="px-3 py-2 border border-dark-border rounded-xl text-sm text-gray-400 hover:bg-dark-surface disabled:opacity-30 bg-dark-card" disabled>{t('common.previous')}</button>
                <button className="px-4 py-2 bg-accent/15 text-accent rounded-xl text-sm font-medium border border-accent/25">1</button>
                <button className="px-3 py-2 border border-dark-border rounded-xl text-sm text-gray-400 hover:bg-dark-surface bg-dark-card">2</button>
                <button className="px-3 py-2 border border-dark-border rounded-xl text-sm text-gray-400 hover:bg-dark-surface bg-dark-card">3</button>
                <button className="px-3 py-2 border border-dark-border rounded-xl text-sm text-gray-400 hover:bg-dark-surface bg-dark-card">{t('common.next')}</button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Shopping;
